import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef } from 'react';
import { SdkRuntimeContext } from '../../provider/SdkRuntimeContext.js';
import { ScreenVisibilityContext } from '../context/ScreenVisibilityContext.js';
import { getCachedQuery } from '../internal/get-cached-query.js';
import { getPollingCoordinator } from './coordinator.js';
export function useQueryPolling(options) {
  const $ = _c(26);
  const client = useQueryClient();
  const runtime = useContext(SdkRuntimeContext);
  const {
    visible
  } = useContext(ScreenVisibilityContext);
  let t0;
  if ($[0] !== options || $[1] !== visible) {
    t0 = {
      options,
      visible
    };
    $[0] = options;
    $[1] = visible;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const latest = useRef(t0);
  let queryHash;
  let t1;
  if ($[3] !== client || $[4] !== options.queryKey) {
    ({
      queryHash
    } = client.defaultQueryOptions({
      queryKey: options.queryKey
    }));
    t1 = client.getQueryCache().get(queryHash);
    $[3] = client;
    $[4] = options.queryKey;
    $[5] = queryHash;
    $[6] = t1;
  } else {
    queryHash = $[5];
    t1 = $[6];
  }
  const cachedQuery = t1;
  const managed = options.policy !== undefined || options.interval !== undefined;
  let t2;
  if ($[7] !== client || $[8] !== managed || $[9] !== options || $[10] !== visible) {
    t2 = () => {
      latest.current = {
        options,
        visible
      };
      if (managed) {
        getPollingCoordinator(client).refreshQuery(options.queryKey);
      }
    };
    $[7] = client;
    $[8] = managed;
    $[9] = options;
    $[10] = visible;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  useEffect(t2);
  let t3;
  let t4;
  if ($[12] !== cachedQuery || $[13] !== client || $[14] !== managed || $[15] !== options.batched || $[16] !== options.enabled || $[17] !== options.fetchOnce || $[18] !== options.hasNats || $[19] !== options.interval || $[20] !== options.operationName || $[21] !== options.policy || $[22] !== queryHash || $[23] !== runtime) {
    t3 = () => {
      if (!managed || options.fetchOnce === true || !options.enabled) {
        return;
      }
      const coordinator = getPollingCoordinator(client);
      const hasNats = options.hasNats ?? options.policy?.hasNats ?? false;
      const policy = options.interval === undefined ? {
        ...options.policy,
        hasNats
      } : {
        hasNats,
        intervalMs: options.interval,
        explicit: true
      };
      const query = cachedQuery ?? getCachedQuery(client, {
        queryKey: latest.current.options.queryKey,
        queryHash
      });
      if (query === undefined) {
        return;
      }
      const release = coordinator.register(query.queryKey, {
        policy,
        batched: options.batched,
        operationName: options.operationName,
        active: () => latest.current.visible && latest.current.options.enabled && (runtime === null || runtime.adapter.appState.current() === "active" && runtime.adapter.netInfo.current().online)
      });
      const releaseApp = runtime?.adapter.appState.addListener(() => coordinator.refreshQuery(query.queryKey));
      const releaseNet = runtime?.adapter.netInfo.addListener(() => coordinator.refreshQuery(query.queryKey));
      return () => {
        releaseApp?.();
        releaseNet?.();
        release();
      };
    };
    t4 = [client, managed, queryHash, cachedQuery, options.policy, options.hasNats, options.interval, options.enabled, options.fetchOnce, options.batched, options.operationName, runtime];
    $[12] = cachedQuery;
    $[13] = client;
    $[14] = managed;
    $[15] = options.batched;
    $[16] = options.enabled;
    $[17] = options.fetchOnce;
    $[18] = options.hasNats;
    $[19] = options.interval;
    $[20] = options.operationName;
    $[21] = options.policy;
    $[22] = queryHash;
    $[23] = runtime;
    $[24] = t3;
    $[25] = t4;
  } else {
    t3 = $[24];
    t4 = $[25];
  }
  useEffect(t3, t4);
  return managed;
}